import { useCallback, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FinalCTA from "../components/FinalCTA";
import HopeNamingNote from "../components/HopeNamingNote";
import {
    CalendarDays,
    Heart,
    MapPin,
    MessageCircle,
    Quote,
    ShieldCheck,
    Sparkles,
    Users,
} from "lucide-react";

const FOUNDER = {
    name: "Chris \"Toon\"",
    role: "Founder, Genesis Recovery AI",
    portrait: `${process.env.PUBLIC_URL || ""}/clientpic.jpg`,
    soberSince: "July 8, 2005",
    location: "Southern California",
    mission: "Building private, compassionate support for people in recovery",
};

const SLIDES = [
    {
        label: "Who I Am",
        heading: "Who I Am",
        image: FOUNDER.portrait,
        imageAlt: "Chris Toon, founder of Genesis Recovery AI",
        body: [
            "My name is Chris; friends call me Cartoon (or Toon). I've been clean and sober since July 8, 2005. When I finally asked for help, I had no clue what was coming. I reached out to an old acquaintance who was sober. She got me into Tarzana Treatment Center right away.",
            "I remember walking in, watching her fill out my paperwork, and looking around at everyone else. My first thought? \"These people look really f*ed up. Let's get out of here.\" She looked at me and said, \"Toon, you look worse.\"",
            "That hit hard. I was in total denial - I couldn't see myself the way others did. But I stayed.",
            "People from outside, members of a 12-step fellowship, came in regularly to share their stories with us. They talked about what they used to be like and what they were like now. Most of it felt like background noise to me... until one guy spoke.",
            "His story sounded exactly like mine: the same ways he used drugs, the same sketchy places he frequented, the same kinds of people he ran with. All of it matched. I sat there thinking, \"Me too.\" But what really got me was hearing how he didn't do any of those things anymore. He had five years clean.",
            "That was the magic of recovery: finally hearing someone whose life looked just like mine say they'd escaped it - and proved it was possible. For the first time, I believed real change could happen for me too.",
            "Before I left Tarzana, a counselor pulled me aside: \"Do you have a plan? Most people who leave without one relapse.\" He connected me to Genesis, an outpatient program a few miles from my parents' house. That's where I met Jody T., the counselor who changed my life forever.",
            "At Genesis, I learned addiction doesn't care who you are. Rich or poor, married or single, mansion or homeless - we all shared the same inner pain. To get free and build a happy, purposeful life, we had to do the real work inside. That's exactly what we did.",
        ],
        closing: "Jody, I love you. Rest in peace. I'm grateful for you more than words can say.",
    },
    {
        label: "My Why",
        heading: "My Why",
        image: FOUNDER.portrait,
        imageAlt: "Chris Toon, founder of Genesis Recovery AI",
        body: [
            "I named this app Genesis Recovery AI in honor of Jody and the Genesis outpatient program that literally saved my life. It was there that the real healing began - the inner work, the community, the hope - and I carry that gratitude every day.",
            "While at Genesis, I had to write a \"breakup letter\" to my addiction. In it, I promised I'd spend the rest of my life helping others escape the same grip.",
            "For years, I saw how lucky I was to live in Southern California. The sober community here is strong, full of meetings, mentors, and fellowship. But so many people around the world don't have that safety net. Isolation makes recovery brutally hard.",
            "That's why I created Genesis Recovery AI. It's a tool to help anyone searching for a way out - wherever they are - find help, connect with others who've walked the path, and build real community.",
        ],
        cards: [
            {
                Icon: Users,
                title: "Community First",
                text: "Recovery isn't a solo journey. We need mentors and friends to walk with us, bounce ideas off, and quiet the noise in our heads. Most of us were isolated for so long; having real connections makes life enjoyable again.",
            },
            {
                Icon: MessageCircle,
                title: "AI as a 24/7 Companion",
                text: "There's so much wisdom out there: clinical research, memoirs, recovery blogs. AI pulls the right answers fast, remembers what you've shared, and supports you anytime, day or night. It's like having a non-judgmental friend who is always there.",
            },
            {
                Icon: ShieldCheck,
                title: "Privacy You Can Trust",
                text: "Your journals in Genesis Recovery AI are fully encrypted and private. Unlike other AI tools, we don't sell your data or target you with ads.",
            },
        ],
        closing:
            "My deepest why? I want to help as many people as possible break free from booze, drugs, and depression so they can truly live life - and give back to the world. If you're reading this and hurting, know this: You're not alone. There is a way out. And I'm here, along with a whole community, to help you find it.",
        signed: true,
    },
];

export default function MyStoryPage() {
    const [active, setActive] = useState(0);

    const switchTo = useCallback((index) => {
        if (index < 0 || index >= SLIDES.length || index === active) return;
        setActive(index);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [active]);

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === "ArrowLeft") switchTo(active - 1);
            if (event.key === "ArrowRight") switchTo(active + 1);
        };

        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active, switchTo]);

    const slide = SLIDES[active];

    return (
        <div className="bg-[#f6f1e8] text-slate-900 min-h-screen">
            <Navbar />

            <main data-testid="my-story-page" className="pt-[7rem]">
                <section className="relative overflow-hidden bg-[#081126] text-white border-b border-white/10">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.2)_0%,_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(96,165,250,0.1)_0%,_transparent_28%)]" />
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-18">
                        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
                            <div className="max-w-3xl">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm font-medium text-white/85">
                                    <Sparkles className="w-4 h-4 text-emerald-300" />
                                    Founder Story
                                </div>

                                <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.96] tracking-tight">
                                    The Story Behind
                                    <span className="block text-emerald-300">Genesis Recovery AI</span>
                                </h1>

                                <p className="mt-6 max-w-2xl text-lg md:text-xl text-white/72 leading-relaxed">
                                    How a &apos;breakup letter&apos; to my addiction in 2005 became the app
                                    you&apos;re looking at today.
                                </p>

                                <HopeNamingNote className="mt-4 max-w-2xl text-white/55" />

                                <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-3xl">
                                    {[
                                        {
                                            Icon: CalendarDays,
                                            label: "Sober Since",
                                            value: FOUNDER.soberSince,
                                        },
                                        {
                                            Icon: MapPin,
                                            label: "Based In",
                                            value: FOUNDER.location,
                                        },
                                        {
                                            Icon: Heart,
                                            label: "Mission",
                                            value: "Private recovery support",
                                        },
                                    ].map((item) => (
                                        <div
                                            key={item.label}
                                            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 backdrop-blur-sm"
                                        >
                                            <div className="flex items-center gap-2 text-emerald-300 mb-2">
                                                <item.Icon className="w-4 h-4" />
                                                <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-white/55">
                                                    {item.label}
                                                </span>
                                            </div>
                                            <div className="text-sm font-medium text-white/90">
                                                {item.value}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="relative">
                                <div className="absolute -inset-4 rounded-[2rem] bg-emerald-400/10 blur-3xl" />
                                <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-white/5 backdrop-blur-sm shadow-[0_25px_80px_rgba(2,8,23,0.45)]">
                                    <img
                                        src={FOUNDER.portrait}
                                        alt={FOUNDER.name}
                                        className="w-full aspect-[4/4.4] object-cover object-top"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-slate-950/85 to-transparent">
                                        <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-semibold">
                                            {FOUNDER.role}
                                        </div>
                                        <div className="mt-2 text-2xl font-semibold text-white">
                                            {FOUNDER.name}
                                        </div>
                                        <div className="mt-2 text-sm text-white/70 max-w-md">
                                            {FOUNDER.mission}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section data-testid={`my-story-content-${active}`} className="relative py-12 md:py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-[320px_minmax(0,1fr)] gap-8 lg:gap-12 items-start">
                            <aside className="lg:sticky lg:top-28">
                                <div className="rounded-[2rem] border border-[#e4dbce] bg-white/85 backdrop-blur-sm p-5 shadow-[0_15px_50px_rgba(43,29,14,0.06)]">
                                    <div
                                        data-testid="my-story-tabs"
                                        role="tablist"
                                        className="inline-flex w-full items-center rounded-full bg-[#f2ece2] p-1"
                                    >
                                        {SLIDES.map((item, index) => (
                                            <button
                                                key={item.label}
                                                role="tab"
                                                aria-selected={index === active}
                                                onClick={() => switchTo(index)}
                                                data-testid={`my-story-tab-${index}`}
                                                className={`flex-1 rounded-full px-4 py-3 text-sm font-semibold transition-all ${
                                                    index === active
                                                        ? "bg-white text-slate-950 shadow-sm"
                                                        : "text-[#766b5e] hover:text-slate-900"
                                                }`}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="mt-5 overflow-hidden rounded-[1.5rem] border border-[#ebe2d7]">
                                        <img
                                            src={slide.image}
                                            alt={slide.imageAlt}
                                            className="w-full aspect-[0.92] object-cover object-top"
                                        />
                                    </div>

                                    <div className="mt-5">
                                        <div className="text-[11px] uppercase tracking-[0.2em] text-[#9d8f7f] font-semibold">
                                            Current chapter
                                        </div>
                                        <h2 className="mt-2 font-serif text-3xl text-[#2e261f]">
                                            {slide.heading}
                                        </h2>
                                        <p className="mt-3 text-sm leading-7 text-[#70665c]">
                                            {active === 0
                                                ? "A deeply personal chapter about the moment recovery became real."
                                                : "Why Genesis exists, and the core ideas the platform is built around."}
                                        </p>
                                    </div>

                                    <div className="mt-6 space-y-3">
                                        {[
                                            "Community first",
                                            "Lived experience, not theory",
                                            "Privacy is the whole point",
                                        ].map((item) => (
                                            <div
                                                key={item}
                                                className="flex items-start gap-3 text-sm text-[#5f564d]"
                                            >
                                                <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-600 shrink-0" />
                                                <span>{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </aside>

                            <article className="min-w-0">
                                <div className="rounded-[2.2rem] border border-[#e5dccf] bg-white/88 backdrop-blur-sm px-6 sm:px-8 md:px-10 lg:px-12 py-8 md:py-10 shadow-[0_15px_60px_rgba(38,24,12,0.05)]">
                                    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-4 py-1.5 text-sm font-semibold">
                                        <Quote className="w-4 h-4" />
                                        Founder chapter
                                    </div>

                                    <h1 className="mt-5 font-serif text-[3rem] md:text-[4rem] lg:text-[4.65rem] leading-[0.95] tracking-tight text-[#2e261f]">
                                        {slide.heading}
                                    </h1>

                                    <div className="mt-6 space-y-5 text-[16px] md:text-[17px] leading-[1.95] text-[#5f564d]">
                                        {slide.body.map((paragraph, index) =>
                                            index === 0 ? (
                                                <p
                                                    key={paragraph}
                                                    className="first-letter:float-left first-letter:font-serif first-letter:text-[4.5rem] first-letter:leading-[0.9] first-letter:mr-3 first-letter:mt-1 first-letter:text-[#2e261f]"
                                                >
                                                    {paragraph}
                                                </p>
                                            ) : (
                                                <p key={paragraph}>{paragraph}</p>
                                            )
                                        )}
                                    </div>

                                    {slide.cards && (
                                        <div data-testid="my-story-pillars" className="mt-10 grid gap-4">
                                            {slide.cards.map((card) => (
                                                <div
                                                    key={card.title}
                                                    data-testid={`my-story-pillar-${card.title.toLowerCase().replace(/\s+/g, "-")}`}
                                                    className="rounded-[1.5rem] border border-[#e9e0d4] bg-[#fcfaf6] px-6 py-5"
                                                >
                                                    <div className="flex items-center gap-3 text-[#2e261f]">
                                                        <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                                                            <card.Icon className="w-5 h-5" />
                                                        </div>
                                                        <h3 className="font-serif text-[1.45rem] font-semibold">
                                                            {card.title}
                                                        </h3>
                                                    </div>
                                                    <p className="mt-3 text-[15px] leading-[1.82] text-[#6a6158]">
                                                        {card.text}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="mt-10 rounded-[1.7rem] border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white px-6 py-6">
                                        <p className="font-serif italic text-[1.18rem] md:text-[1.26rem] leading-[1.9] text-[#352c24]">
                                            {slide.closing}
                                        </p>

                                        {slide.signed && (
                                            <div className="mt-10 text-right">
                                                <div className="font-serif italic text-[2.2rem] leading-none text-[#2e261f]">
                                                    - Chris
                                                </div>
                                                <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[#a99b8a] font-semibold">
                                                    {FOUNDER.role}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </article>
                        </div>
                    </div>
                </section>

                <FinalCTA />
            </main>

            <Footer />
        </div>
    );
}
